import {Span} from '../../search/search-types';

// PDF user space has a BOTTOM-LEFT origin; CSS has a TOP-LEFT origin.
// Boxes are expressed as PERCENTAGES of the page so they are scale-independent (zoom is free).

export interface PctBox {
    leftPct: number;
    topPct: number;
    widthPct: number;
    heightPct: number;
}

function isFiniteNumber(value: number | undefined): boolean {
    return typeof value === 'number' && Number.isFinite(value);
}

/**
 * True if the span carries the geometry needed to position it. `Span` types these as required, but the
 * backend omits them for tag spans it could not place, so they are checked rather than trusted.
 */
export function hasPdfCoordinates(span: Span): boolean {
    return isFiniteNumber(span.pageNumber) && span.pageNumber > 0
        && isFiniteNumber(span.bottomLeftX) && isFiniteNumber(span.bottomLeftY)
        && isFiniteNumber(span.width) && isFiniteNumber(span.height);
}

/** A real, strictly positive dimension, i.e. one safe to divide by. */
export function isPositiveFinite(value: number): boolean {
    return Number.isFinite(value) && value > 0;
}

export interface RectLike {
    bottomLeftX: number;
    bottomLeftY: number;
    width: number;
    height: number;
    pageWidth: number;
    pageHeight: number;
}

/**
 * PDF-space rect -> CSS percentage box (top-left origin), accounting for the page `rotate`
 * (0/90/180/270, clockwise — matching pdf.js). Percentages are relative to the *rendered* page box,
 * whose dimensions swap at 90/270, so the fractions below are already in the rotated frame.
 */
export function spanToBoxPct(s: RectLike, rotate = 0): PctBox {
    // unrotated fractions, top-left origin
    const Lf = s.bottomLeftX / s.pageWidth;
    const Tf = (s.pageHeight - s.bottomLeftY - s.height) / s.pageHeight;
    const Wf = s.width / s.pageWidth;
    const Hf = s.height / s.pageHeight;

    let l = Lf, t = Tf, w = Wf, h = Hf;
    const r = ((rotate % 360) + 360) % 360;
    if (r === 90) {
        l = 1 - (Tf + Hf);
        t = Lf;
        w = Hf;
        h = Wf;
    } else if (r === 180) {
        l = 1 - (Lf + Wf);
        t = 1 - (Tf + Hf);
        w = Wf;
        h = Hf;
    } else if (r === 270) {
        l = Tf;
        t = 1 - (Lf + Wf);
        w = Hf;
        h = Wf;
    }

    return {leftPct: l * 100, topPct: t * 100, widthPct: w * 100, heightPct: h * 100};
}

/**
 * Grows a tag span: rounded outward to whole points, then 1pt more below and 1pt more above. Without it a
 * tag box hugs the text from the baseline to the top of the capitals. PDFBox fills about the same area for
 * a highlight annotation (0.5pt below, 1pt above). Query spans are not grown: they are the rectangles of
 * the highlight annotations the server built, which already reach 0.5pt past the rounded hit on the left
 * and below, and 1pt on the right and above.
 */
export function tagHighlightArea<T extends RectLike>(s: T): T {
    const left = Math.floor(s.bottomLeftX);
    const right = Math.ceil(s.bottomLeftX + s.width);
    const bottom = Math.floor(s.bottomLeftY) - 1;
    const top = Math.ceil(s.bottomLeftY + s.height) + 1;
    return {...s, bottomLeftX: left, bottomLeftY: bottom, width: right - left, height: top - bottom};
}

/** A page's pdf.js view box, unrotated. */
export interface PageViewBox {
    w: number;
    h: number;
    x: number;
    y: number;
}
